import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Sun, Calendar, Wallet, MapPin, Compass, AlertTriangle } from 'lucide-react';

const tips = [
  { title: '防晒', content: '高原紫外线强，务必做好防晒措施' },
  { title: '高反', content: '初到高原避免剧烈运动，多喝水' },
];

const pois = [
  { name: '洱海', category: '自然景观' },
  { name: '大理古城', category: '人文古迹' },
];

const travelogues = [
  { title: '大理七日慢生活', author: '旅行者A' },
];

const guides = [
  { title: '大理深度游攻略', days: 5 },
];

const DestinationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="h-[300px] rounded-card overflow-hidden relative mb-6 shadow-card">
          <img src={`https://picsum.photos/seed/${id}-scenery/1200/600`} alt="" className="w-full h-full mx-auto object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <Button variant="ghost" onClick={() => navigate('/destinations')} className="absolute top-4 left-4 text-white hover:text-white/80"><ArrowLeft className="w-4 h-4 mr-1" /> 返回</Button>
          <div className="absolute bottom-6 left-6 text-white">
            <h1 className="text-3xl font-bold mb-1">{id || '目的地'}</h1>
            <p className="text-sm opacity-80">中国 · 云南</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-6 -mt-12 relative z-10 mx-4">
          <Card className="border-0 shadow-card rounded-card bg-white flex-1 min-w-[140px]">
            <CardContent className="p-4 text-center">
              <Sun className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xs text-text-tertiary">最佳季节</p>
              <p className="text-sm font-medium text-text-primary">3-5月 / 9-11月</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card rounded-card bg-white flex-1 min-w-[140px]">
            <CardContent className="p-4 text-center">
              <Calendar className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xs text-text-tertiary">建议天数</p>
              <p className="text-sm font-medium text-text-primary">5-7天</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card rounded-card bg-white flex-1 min-w-[140px]">
            <CardContent className="p-4 text-center">
              <Wallet className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xs text-text-tertiary">预算范围</p>
              <p className="text-sm font-medium text-text-primary">¥2000-4000</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="概览" className="mb-6">
          <TabsList className="bg-white rounded-btn p-1 shadow-card">
            {['概览', '景点', '游记', '攻略', '避坑'].map((t) => <TabsTrigger key={t} value={t} className="rounded-btn px-4 data-[state=active]:bg-primary data-[state=active]:text-white">{t}</TabsTrigger>)}
          </TabsList>

          <div className="mt-4">
            <Card className="border-0 shadow-card rounded-card bg-white mb-6">
              <CardContent className="p-6">
                <h2 className="font-bold text-text-primary mb-3">目的地介绍</h2>
                <p className="text-sm text-text-secondary leading-relaxed">{id || '这里'}是一座充满魅力的城市，拥有丰富的自然景观和深厚的文化底蕴，是旅行者心中的圣地。</p>
              </CardContent>
            </Card>

            <div className="mb-6">
              <h2 className="font-bold text-text-primary mb-4">推荐景点</h2>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {pois.map((p) => (
                  <Card key={p.name} className="min-w-[160px] border-0 shadow-card rounded-card bg-white cursor-pointer" onClick={() => navigate('/pois')}>
                    <div className="h-24 bg-bg-secondary rounded-t-card">
                      <img src={`https://picsum.photos/seed/${p.name}/300/200`} alt="" className="w-full h-full mx-auto object-cover rounded-t-card" />
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm font-medium text-text-primary">{p.name}</p>
                      <Badge className="bg-primary-light text-primary rounded-full text-[10px] mt-1">{p.category}</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h2 className="font-bold text-text-primary mb-4">避坑贴士</h2>
              <div className="space-y-3">
                {tips.map((t, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white rounded-card shadow-card p-4">
                    <AlertTriangle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-text-primary">{t.title}</p>
                      <p className="text-xs text-text-secondary mt-1">{t.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tabs>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8E4DF] p-4 flex gap-3 z-40 md:static md:bg-transparent md:border-0 md:p-0 md:mt-8">
          <Button onClick={() => navigate('/planner')} className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-btn shadow-btn"><MapPin className="w-4 h-4 mr-1" /> 创建行程</Button>
          <Button variant="outline" onClick={() => navigate('/guides')} className="flex-1 rounded-btn border-[#E8E4DF]"><Compass className="w-4 h-4 mr-1" /> 查看攻略</Button>
        </div>
      </div>
    </div>
  );
};

export default DestinationDetail;
